// A project's workspace (/projects/<code>): one header row (its basics and readiness), the setup steps as tabs (?tab=), an
// item or a part's spec in the side pane (?open=<item id>, ?spec=<item id> open them there), and the basics in a dialog
// (?edit=basics). A run (?tab=productions&run=PR-001) is a level of its own: its header (with the way back to the
// project) and its tabs take the page, so a run never sits under the project's header and tabs (Mitch, 2026-11-03:
// "Getting into a run is also messy and it's not very coherent"). Every tab is built from ui/PageParts.tsx: a strip of
// figures, then plain sections, each one table or one box.
// Everything is from synced rows through shared/projects.ts, worked out once per change (useProjectIndex), so tabs
// switch instantly.
import { ArrowRight, ChevronRight, Pencil } from "lucide-react";
import { type ComponentProps, type ReactNode, useCallback, useEffect } from "react";
import type { Project } from "../../../../shared/db.gen.ts";
import {
  type Gap,
  type ProjectIndex,
  type ProjectScope,
  projectScope,
  type Readiness,
  readiness,
  STEP_STATUS_LABELS,
  type Step,
  type StepKey,
  type StepStatus,
} from "../../../../shared/projects.ts";
import { cost2 } from "../../lib/boms.ts";
import { money, options, PROJECT_STATUS_LABELS } from "../../lib/format.ts";
import { can } from "../../lib/mutate.ts";
import { gapLink, useProjectIndex } from "../../lib/projects.ts";
import { Link, setSearch, usePath, useSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button, cx } from "../../ui/controls.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { Figure, Figures, PageSection } from "../../ui/PageParts.tsx";
import { Fields } from "../../ui/RecordPanel.tsx";
import { PaneLink, usePaneParam } from "../../ui/SidePane.tsx";
import { BomEditor } from "../BomEditor.tsx"; // a BOM opens in the Build tab, keeping the project
import { type FlowRoot, ProductFlow } from "../flow/ProductFlowPage.tsx"; // Product flow ---
import { BuildTab } from "./BuildTab.tsx";
import { CostsTab } from "./CostsTab.tsx"; // Production runs --- the cost stack, waterfall, what-if, tooling
import { FilesTab, NO_PARTS, SourcingTab } from "./PartsTabs.tsx";
import { ChangesTab, ProductionTab } from "./ProductionTab.tsx";
import { ProductsTab } from "./ProductsTab.tsx";
import { PROJECT_STATUS_TONE, ReadinessBar, StatusIcon } from "./ProjectsPage.tsx";
import { RunsTab } from "./runs/RunsTab.tsx"; // Production runs ---

export const TABS = ["overview", "products", "flow", "build", "sourcing", "files", "costs", "production", "productions", "changes"] as const;
export type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = {
  overview: "Overview",
  products: "Products",
  flow: "Product flow", // Product flow --- how each of the project's products is made, drawn (pages/flow)
  build: "Build",
  sourcing: "Sourcing",
  files: "Specs & files",
  costs: "Costs",
  production: "CM & stock",
  productions: "Runs", // Production runs --- PR-001…: plan, orders, costs, timeline, output, result
  changes: "Changes",
};
/** The setup steps each tab shows (Build covers the sub-assemblies too). */
const TAB_STEPS: Partial<Record<Tab, StepKey[]>> = {
  products: ["products"],
  build: ["build", "subassemblies"],
  sourcing: ["sourcing"],
  files: ["files"],
  costs: ["costs"],
  production: ["production"],
};
/** Where each step is worked on. */
export const STEP_TAB: Record<StepKey, Tab> = {
  basics: "overview",
  products: "products",
  build: "build",
  subassemblies: "build",
  sourcing: "sourcing",
  files: "files",
  costs: "costs",
  production: "production",
};
const RANK: Record<StepStatus, number> = { needs_work: 0, not_started: 1, done: 2 };
const worstOf = (steps: Step[]) => steps.reduce<StepStatus>((w, s) => (RANK[s.status] < RANK[w] ? s.status : w), "done");

export type TabProps = { project: Project; px: ProjectIndex; scope: ProjectScope; r: Readiness; here: string };

export function Workspace({ project }: { project: Project }) {
  const px = useProjectIndex();
  const path = usePath();
  const params = useSearch();
  const tab = (TABS as readonly string[]).includes(params.get("tab") ?? "") ? (params.get("tab") as Tab) : "overview";
  // Side pane --- an item opens in the side pane: every tab's ?open=<item id> (a gap's link, a part's row) lands there
  usePaneParam("open", "item");
  usePaneParam("spec", "spec");
  const editing = params.get("edit") === "basics";
  const bom = tab === "build" ? params.get("bom") : null; // a BOM open in the Build tab (lib/boms.ts bomPlaceFor)
  const runs = useRows("production_run"); // Production runs ---
  const scope = projectScope(px, project.id);
  const r = readiness(px, project.id);
  const here = `${path}${tab === "overview" ? "" : `?tab=${tab}`}`;

  if (!scope || !r) return null;
  const props: TabProps = { project, px, scope, r, here };
  // A run open: its own header and tabs take the page (RunsTab → RunWorkspace), the project one step back in its crumbs
  const want = tab === "productions" ? params.get("run") : null;
  const inRun = !!want && runs.some((x) => x.project_id === project.id && (x.number === want || x.id === want));
  if (inRun)
    return (
      <div className="flex h-full min-h-0 flex-col">
        <RunsTab {...props} />
        {editing && <BasicsDialog project={project} onClose={() => setSearch({ edit: null })} />}
      </div>
    );
  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <Header project={project} r={r} px={px} />
        <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-line bg-surface px-3 text-[12px]">
          {TABS.map((t) => {
            const steps = (TAB_STEPS[t] ?? []).map((k) => r.steps.find((s) => s.key === k)).filter((s): s is Step => !!s);
            const status = steps.length ? worstOf(steps) : null;
            const drafts = t === "changes" ? r.notices.filter((n) => n.status === "draft").length : 0;
            return (
              <button
                key={t}
                type="button"
                onClick={() =>
                  setSearch({
                    tab: t === "overview" ? null : t,
                    open: null,
                    run: null,
                    rt: null,
                    item: null,
                    build: null,
                    bom: null,
                    btab: null,
                    variant: null,
                  })
                }
                title={status ? STEP_STATUS_LABELS[status] : undefined}
                className={cx(
                  "-mb-px flex items-center gap-1.5 border-b-2 px-2 py-2 whitespace-nowrap",
                  tab === t ? "border-accent text-text" : "border-transparent text-muted hover:text-text",
                )}
              >
                {status && <StatusIcon status={status} className="size-3.5" />}
                {TAB_LABELS[t]}
                {t === "overview" && (
                  <span className="text-faint">
                    {r.ready}/{r.total}
                  </span>
                )}
                {t === "productions" && runs.some((x) => x.project_id === project.id) && (
                  <span className="text-faint">
                    {runs.filter((x) => x.project_id === project.id).length}
                    {runs.some((x) => x.project_id === project.id && x.status !== "closed")
                      ? ` · ${runs.filter((x) => x.project_id === project.id && x.status !== "closed").length} open`
                      : ""}
                  </span>
                )}
                {t === "changes" && r.notices.length > 0 && (
                  <span className="text-faint">
                    {r.notices.length}
                    {drafts ? ` · ${drafts} draft` : ""}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <div className={cx("min-h-0 flex-1 bg-bg", tab === "flow" || bom ? "overflow-hidden" : "overflow-y-auto")}>
          {tab === "overview" && <Overview {...props} />}
          {tab === "products" && <ProductsTab {...props} />}
          {tab === "flow" && <FlowTab {...props} />}
          {tab === "build" && (bom ? <BomEditor key={bom} id={bom} /> : <BuildTab {...props} />)}
          {tab === "sourcing" && <SourcingTab {...props} />}
          {tab === "files" && <FilesTab {...props} />}
          {tab === "costs" && <CostsTab {...props} />}
          {tab === "production" && <ProductionTab {...props} />}
          {tab === "productions" && <RunsTab {...props} />}
          {tab === "changes" && <ChangesTab {...props} />}
        </div>
      </div>
      {editing && <BasicsDialog project={project} onClose={() => setSearch({ edit: null })} />}
    </div>
  );
}

/** Product flow --- the project's products (any variant in it) and the sub-assemblies nothing else uses (a kit, a dongle). */
function FlowTab({ scope }: TabProps) {
  const only = useCallback(
    (root: FlowRoot) =>
      root.variants.some((v) => scope.variantIds.has(v.id)) ||
      scope.families.some((f) => f.kind === "subassembly" && f.via === "top" && f.bom.item_id === root.variants[0]?.id),
    [scope],
  );
  return <ProductFlow only={only} />;
}

function Header({ project, r, px }: { project: Project; r: Readiness; px: ProjectIndex }) {
  const users = useRows("app_user");
  const cm = project.cm_vendor_id ? px.vendorById.get(project.cm_vendor_id) : undefined;
  const owner = users.find((u) => u.id === project.owner_id);
  const none = (text: string) => <span className="text-faint">{text}</span>;
  const facts: [string, ReactNode][] = [
    [
      "CM",
      cm ? (
        <PaneLink kind="vendor" id={cm.id} key="cm" to={`/vendors?open=${cm.id}`}>
          {cm.short_name || cm.name}
        </PaneLink>
      ) : (
        none("not chosen")
      ),
    ],
    ["Launch", project.launch_date ? String(project.launch_date).slice(0, 10) : none("no date")],
    ["Owner", owner ? owner.name || owner.email : none("nobody")],
  ];
  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface px-4">
      <Link to="/projects" className="text-muted hover:text-text">
        Projects
      </Link>
      <ChevronRight className="size-3.5 shrink-0 text-faint" />
      <h1 className="min-w-0 truncate text-[15px] font-semibold">{project.name}</h1>
      {project.code && <span className="font-mono text-[12px] text-muted">{project.code}</span>}
      <Badge tone={PROJECT_STATUS_TONE[project.status]}>{PROJECT_STATUS_LABELS[project.status]}</Badge>
      <div className="ml-3 flex min-w-0 items-center gap-3 overflow-hidden text-[12px]">
        {facts.map(([label, value]) => (
          <span key={label} className="whitespace-nowrap">
            <span className="text-faint">{label} </span>
            <span className="[&_a]:hover:underline">{value}</span>
          </span>
        ))}
      </div>
      {/* Readiness in a word and a bar; the Overview tab has the steps */}
      <button
        type="button"
        onClick={() => setSearch({ tab: null, run: null, rt: null })}
        title={r.summary}
        className="ml-auto flex shrink-0 items-center gap-2 rounded px-1.5 py-1 text-[12px] hover:bg-hover"
      >
        <ReadinessBar r={r} className="w-24" />
        <span className="whitespace-nowrap">
          <span className="font-medium">{r.ready}</span>
          <span className="text-muted"> of {r.total} ready</span>
        </span>
      </button>
      {can("project.update") && (
        <Button size="sm" variant="ghost" icon={<Pencil className="size-3.5" />} onClick={() => setSearch({ edit: "basics" })}>
          Basics
        </Button>
      )}
    </header>
  );
}

function BasicsDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const vendors = useRows("vendor");
  const users = useRows("app_user");
  const cms = vendors
    .filter((v) => !v.archived || v.id === project.cm_vendor_id)
    .sort((a, b) => Number(a.kind === "supplier") - Number(b.kind === "supplier") || (a.short_name || a.name).localeCompare(b.short_name || b.name));
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && document.querySelectorAll("[data-dialog]").length === 1) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-[480px] rounded-xl border border-line bg-surface p-3 shadow-pop">
        <div className="mb-1 px-2 text-[15px] font-semibold">Basics</div>
        <div className="mb-2 px-2 text-[12px] text-muted">Each change saves as you make it.</div>
        <div className="flex flex-col gap-0.5">
          <Fields
            row={project}
            update="project.update"
            disabled={!can("project.update")}
            fields={[
              { key: "name", label: "Name" },
              { key: "code", label: "SKU prefix", mono: true, placeholder: "e.g. GCU2" },
              { key: "status", label: "Status", type: "select", options: options(PROJECT_STATUS_LABELS) },
              {
                key: "cm_vendor_id",
                label: "CM",
                type: "select",
                nullable: true,
                options: [{ value: "", label: "Not chosen yet" }, ...cms.map((v) => ({ value: v.id, label: v.short_name || v.name }))],
              },
              { key: "launch_date", label: "Target launch", type: "date" },
              {
                key: "owner_id",
                label: "Owner",
                type: "select",
                nullable: true,
                options: [
                  { value: "", label: "Nobody yet" },
                  ...users
                    .filter((u) => (u.active && u.role !== "receiving") || u.id === project.owner_id)
                    .map((u) => ({ value: u.id, label: u.name || u.email })),
                ],
              },
              { key: "notes", label: "Notes", type: "textarea" },
            ]}
          />
        </div>
        <div className="mt-3 flex justify-end px-2">
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </DialogLayer>
  );
}

// Overview ------------------------------------------------------------------------------------------

function Overview({ project, px, r, here }: TabProps) {
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4 p-4">
      <KeyNumbers r={r} />
      <PageSection title="Setup steps" note="In order: each shows what's in place and what's missing" boxed>
        <ol>
          {r.steps.map((s, n) => (
            <li key={s.key} className="border-b border-line px-4 py-2.5 last:border-0">
              <div className="flex items-center gap-2">
                <StatusIcon status={s.status} />
                <span className="w-4 text-right text-[12px] text-faint">{n + 1}</span>
                <span className="font-medium">{s.label}</span>
                <span className="text-[12px] text-muted">{STEP_STATUS_LABELS[s.status]}</span>
                {s.have.length > 0 && <span className="min-w-0 truncate text-[12px] text-faint">· {s.have.join(" · ")}</span>}
                {STEP_TAB[s.key] !== "overview" && (
                  <Link
                    to={`${here.split("?")[0]}?tab=${STEP_TAB[s.key]}`}
                    className="ml-auto flex shrink-0 items-center gap-1 text-[12px] text-accent-text hover:underline"
                  >
                    Open <ArrowRight className="size-3" />
                  </Link>
                )}
              </div>
              {s.gaps.length > 0 && (
                <ul className="mt-1 ml-12 flex flex-col gap-0.5">
                  {s.gaps.map((g) => (
                    <GapRow key={g.key} gap={g} project={project} px={px} here={here} step={s.key} />
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      </PageSection>
    </div>
  );
}

function GapRow({ gap, project, px, here, step }: { gap: Gap; project: Project; px: ProjectIndex; here: string; step: StepKey }) {
  const shown = gap.items.slice(0, 4);
  const tabLink = `${here.split("?")[0]}?tab=${STEP_TAB[step]}`;
  return (
    <li className="text-[12px]">
      <span className={gap.optional ? "text-muted" : ""}>{gap.text}</span>
      {gap.optional && <span className="text-faint"> (suggested)</span>}
      {gap.target && (
        <Link to={gapLink(px, project, gap.target, here)} className="ml-2 text-accent-text hover:underline">
          Fix
        </Link>
      )}
      {shown.length > 0 && (
        <span className="text-faint">
          {": "}
          {shown.map((i, n) => (
            <span key={i.id}>
              {n > 0 && ", "}
              <Link to={gapLink(px, project, i.target, here)} className="text-muted hover:text-text hover:underline" title={i.detail}>
                {i.sku || i.label}
              </Link>
            </span>
          ))}
          {gap.items.length > shown.length && (
            <>
              {", "}
              <Link to={tabLink} className="text-accent-text hover:underline">
                {gap.items.length - shown.length} more
              </Link>
            </>
          )}
        </span>
      )}
    </li>
  );
}

/** The project's key numbers, one strip (Figures), above its steps. */
function KeyNumbers({ r }: { r: Readiness }) {
  const n = r.numbers;
  const pct = (x: number) => `${Math.round(x * 100)}%`;
  const range = (x: { min: number; max: number } | null, f: (v: number) => string) => (!x ? "—" : f(x.min) === f(x.max) ? f(x.min) : `${f(x.min)}–${f(x.max)}`);
  const missing = r.costs.some((c) => !c.complete);
  return (
    <Figures>
      <Figure label="Variants" value={String(n.variants)} sub={`${n.products} ${n.products === 1 ? "product" : "products"}`} />
      <Figure
        label="Parts"
        value={String(n.parts)}
        sub={n.parts ? `${n.materials} materials · ${n.subassemblies} sub-assemblies` : undefined}
        title={n.parts ? `${n.materials} materials · ${n.subassemblies} sub-assemblies · ${n.services} services` : NO_PARTS}
      />
      <Figure label="BOMs" value={String(n.builds)} sub={n.builds ? `${n.builds - n.drafts} released · ${n.drafts} draft` : undefined} />
      <Figure label="Specs released" value={`${n.specsReleased} of ${n.specsNeeded}`} />
      <Figure
        label="Cost per unit"
        value={range(n.cost, (v) => cost2(v))}
        sub={missing ? "some missing" : undefined}
        tone={missing ? "warn" : undefined}
        title={missing ? "Some variants are missing costs" : undefined}
      />
      <Figure label="Margin" value={range(n.margin, pct)} title="The roll-up cost against the Shopify price" />
      <Figure
        label="Consigned at the CM"
        value={money(Math.round(n.consignedValue))}
        sub={n.consignedMissing ? `${n.consignedMissing} without a cost` : undefined}
        tone={n.consignedMissing ? "warn" : undefined}
      />
      <Figure label="Make orders" value={String(n.makeOrders)} sub={n.makeOrders ? `${n.openMakeOrders} open` : undefined} />
    </Figures>
  );
}

/** A section of a tab: its heading, then its content (ui/PageParts.tsx `PageSection`; `boxed` for what isn't a table). */
export function Card(props: ComponentProps<typeof PageSection>) {
  return <PageSection {...props} boxed={props.boxed ?? true} />;
}

/** How many of a gap's items a tab's strip names (the rest: "N more"; the Overview lists four). */
const GAP_NAMES = 6;

/** The gaps of a tab's steps, above its list, each naming what it's about. */
export function StepGaps({ r, steps, project, px, here }: { r: Readiness; steps: StepKey[]; project: Project; px: ProjectIndex; here: string }) {
  const shown = r.steps.filter((s) => steps.includes(s.key));
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1">
      {shown.map((s) => (
        // The step and its status stay on one line; what's missing wraps after them
        <span key={s.key} className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-[12px]">
          <StatusIcon status={s.status} className="size-3.5 shrink-0 self-center" />
          <span className="font-medium whitespace-nowrap">{s.label}</span>
          <span className="whitespace-nowrap text-muted">{STEP_STATUS_LABELS[s.status]}</span>
          {s.gaps
            .filter((g) => !g.optional)
            .map((g) => (
              <span key={g.key} className="text-muted">
                ·{" "}
                {g.target ? (
                  <Link to={gapLink(px, project, g.target, here)} className="hover:underline">
                    {g.text}
                  </Link>
                ) : (
                  g.text
                )}
                {g.items.length > 0 && (
                  <>
                    {": "}
                    {g.items.slice(0, GAP_NAMES).map((i, n) => (
                      <span key={i.id}>
                        {n > 0 && ", "}
                        <Link to={gapLink(px, project, i.target, here)} className="text-text hover:underline" title={i.detail}>
                          {i.sku || i.label}
                        </Link>
                      </span>
                    ))}
                    {g.items.length > GAP_NAMES && <span className="text-faint">{`, ${g.items.length - GAP_NAMES} more`}</span>}
                  </>
                )}
              </span>
            ))}
        </span>
      ))}
    </div>
  );
}
